/**
 * L0 domain tiles (altitude L0, dashboard design rules §1/§7).
 *
 * One tile per headline fact, across the whole estate: Azure alerts, subscription
 * reachability, month-to-date cloud cost, unused licence seats, websites up, M365
 * service health, Cato sites connected, email-security control health, the two
 * Dynamics F&O workspaces, and the three F&O observability figures — slow
 * interactions, browser coverage, open user reports. Every tile is a door — the
 * whole card links into the board that *owns* the number — and no tile is a dead
 * aggregate (§4.1).
 *
 * **Why fourteen tiles and not six.** §1 caps the strip at "~6 numbers", and it
 * still is: most of these carry no number at all, because most of the boards are
 * dark today. The cap is on *figures a manager has to read*, not on doors, and a
 * board left off the row entirely reads as a smaller, healthier estate than the
 * real one — the flattering-omission failure §8 exists to prevent. When the dark
 * boards light up, the strip should be pruned back to one tile per domain (Cloud
 * currently spends three of them) rather than grown.
 *
 * **Each door goes to the number's owner, not to F&O generally.** Slow
 * interactions open `/dynamics#fo-slow`, browser coverage opens `/experience`
 * (which owns every browser-measured figure), and open reports open
 * `/dynamics/reports`. A tile that landed on a board which does not draw its
 * figure is a door onto a hunt.
 *
 * **Honesty (§5) is the whole design here.** A tile has three appearances and
 * they are never confusable: a measured number in an outlined card, a *dark*
 * recessed card with an em dash and a badge saying why, or a skeleton while the
 * first fetch is in flight. See `./tile-state.ts` for the contract; the rule it
 * enforces is that "we measured zero" and "nobody could measure" must not look
 * alike, because this is the page that gets screenshotted into a deck.
 *
 * **Nothing here derives a second opinion.** Each board already owns the mapping
 * from its own states to copy and tone, so the tiles import those mappings rather
 * than re-deriving them: `ACCESS_COPY` and `HEALTH_TONE` from
 * `features/dynamics/status`, `figuresAreCurrent`/`reachedUserTone` from
 * `features/security/status`, `STATE_TONE`/`stateCounts` from
 * `features/network/status`, `rumRolloutState`/`formatCoverage` from
 * `features/dynamics/console-format`. A tile can therefore never disagree with
 * the board it links to.
 *
 * The three F&O observability tiles keep their decisions in `./fo-tile-format.ts`
 * instead of in this file's JSX, because those decisions have flattering failure
 * modes — a 403 read as an empty queue, an un-installed extension read as 0 %
 * coverage — and a pure module can be tested (`tests/fo-tile-format.test.mjs`).
 */

import {
  CircleEllipsis,
  CloudCog,
  Icon,
  type IconComponent,
  type IconTone,
  KeyRound,
  Lock,
  ServerOff,
} from "@norican/icons";
import {
  Badge,
  Card,
  MiniSparkline,
  Skeleton,
  Statistic,
  type StatisticTone,
} from "@norican/react";
import { Link } from "react-router";

import {
  apiErrorStatus,
  DEFAULT_REPORT_LOOKBACK,
  formatCoverage,
  reportLookbackOption,
  rumRolloutState,
} from "@/features/dynamics/console-format";
import {
  AVAILABILITY_ANCHOR,
  DYNAMICS_PATH,
  DYNAMICS_REPORTS_PATH,
  INTEGRATION_ANCHOR,
  SLOW_ANCHOR,
} from "@/features/dynamics/params";
import { ACCESS_COPY, HEALTH_LABEL, HEALTH_ORDER, HEALTH_TONE } from "@/features/dynamics/status";
import { EXPERIENCE_PATH } from "@/features/experience/params";
import { STATE_TONE, stateCounts } from "@/features/network/status";
import { figuresAreCurrent, reachedUserTone } from "@/features/security/status";
import type {
  CloudCost,
  CloudOverview,
  FoConsoleReportList,
  FoConsoleRumStatus,
  FoConsoleSlowSummary,
  FoIntegration,
  FoOverview,
  LicenseOverview,
  M365ServiceHealth,
  MonitoringOverview,
  NetworkOverview,
  SecurityOverview,
} from "@/lib/api";
import {
  formatCount,
  formatCurrency,
  formatCurrencyCompact,
  formatPercent,
} from "@/lib/charts/config";

import type { FeedState } from "./feed-state";
import {
  ACCESS_STATE_TILE,
  browserCoverageTile,
  type DarkIcon,
  type FoTileFacts,
  openReportsTile,
  slowInteractionsTile,
} from "./fo-tile-format";
import { darkTile, resolveTile, type TileContent } from "./tile-state";

interface TileSpec {
  key: string;
  /** The L1 board this tile opens. */
  to: string;
  label: string;
  ariaLabel: string;
  state: TileContent | "pending";
}

interface DomainTilesProps {
  cloud: FeedState<CloudOverview>;
  cost: FeedState<CloudCost>;
  licences: FeedState<LicenseOverview>;
  websites: FeedState<MonitoringOverview>;
  m365: FeedState<M365ServiceHealth>;
  network: FeedState<NetworkOverview>;
  security: FeedState<SecurityOverview>;
  foTelemetry: FeedState<FoOverview>;
  foIntegration: FeedState<FoIntegration>;
  /** The console's 24 h slow-interaction aggregate. */
  foSlow: FeedState<FoConsoleSlowSummary>;
  /** Whether the browser extension reports, and for what share of F&O users. */
  foRum: FeedState<FoConsoleRumStatus>;
  /**
   * The user-report queue. Carried with its thrown error, because a 403 here is
   * a *state* ("not on the investigator list") rather than a failure, and
   * `FeedState` flattens both into `isError`.
   */
  foReports: FeedState<FoConsoleReportList> & { error?: unknown };
}

export function DomainTiles({
  cloud,
  cost,
  licences,
  websites,
  m365,
  network,
  security,
  foTelemetry,
  foIntegration,
  foSlow,
  foRum,
  foReports,
}: DomainTilesProps) {
  const tiles: TileSpec[] = [
    {
      key: "alerts",
      to: "/cloud",
      label: "Firing alerts",
      ariaLabel: "Firing Azure alerts — open the Cloud board",
      state: resolveTile(cloud, alertsTile),
    },
    {
      key: "subscriptions",
      to: "/cloud",
      label: "Subscriptions reachable",
      ariaLabel: "Azure subscriptions reachable — open the Cloud board",
      state: resolveTile(cloud, subscriptionsTile),
    },
    {
      key: "cost",
      to: "/cloud",
      label: "Azure cost (MTD)",
      ariaLabel: "Month-to-date Azure cost — open the Cloud board",
      state: resolveTile(cost, costTile),
    },
    {
      key: "seats",
      to: "/licences",
      label: "Unused licence seats",
      ariaLabel: "Unused licence seats — open the Licences board",
      state: resolveTile(licences, seatsTile),
    },
    {
      key: "websites",
      to: "/websites",
      label: "Websites up",
      ariaLabel: "Websites up — open the Websites board",
      state: resolveTile(websites, websitesTile),
    },
    {
      key: "m365",
      to: "/m365",
      label: "M365 services healthy",
      ariaLabel: "Microsoft 365 service health — open the M365 board",
      state: resolveTile(m365, m365Tile),
    },
    {
      key: "network",
      to: "/network",
      label: "Cato sites connected",
      ariaLabel: "Cato SASE sites connected — open the Network board",
      state: resolveTile(network, networkTile),
    },
    {
      key: "security",
      to: "/security",
      label: "Reached a user",
      ariaLabel: "Dangerous email that reached a user — open the Security board",
      state: resolveTile(security, securityTile),
    },
    {
      key: "fo-telemetry",
      to: `${DYNAMICS_PATH}#${AVAILABILITY_ANCHOR}`,
      label: "F&O environments answering",
      ariaLabel:
        "Dynamics 365 F&O environments answering the synthetic probe — open the Dynamics board",
      state: resolveTile(foTelemetry, foTelemetryTile),
    },
    {
      key: "fo-integration",
      to: `${DYNAMICS_PATH}#${INTEGRATION_ANCHOR}`,
      label: "F&O queues healthy",
      ariaLabel: "Dynamics 365 F&O Service Bus queue health — open the Dynamics board",
      state: resolveTile(foIntegration, foIntegrationTile),
    },
    // The three tiles the F&O observability work added. Each is a door into the
    // board that owns the number — the split into `/dynamics`, browser coverage
    // into `/experience`, the queue into its own screen — so no figure here is
    // the only place it appears.
    {
      key: "fo-slow",
      to: `${DYNAMICS_PATH}#${SLOW_ANCHOR}`,
      label: "Slow interactions (24 h)",
      ariaLabel:
        "F&O slow interactions in the last 24 hours, by the layer that caused them — open the Dynamics board",
      state: resolveTile(foSlow, foSlowTile),
    },
    {
      key: "fo-browser-coverage",
      to: EXPERIENCE_PATH,
      label: "Browser coverage",
      ariaLabel:
        "Share of active F&O users whose browser reports through the extension — open the Application experience board",
      state: resolveTile(foRum, foBrowserCoverageTile),
    },
    {
      key: "fo-reports",
      to: DYNAMICS_REPORTS_PATH,
      label: "Open reports",
      ariaLabel: "User reports of application slowness awaiting triage — open the report queue",
      // Not `resolveTile`: its "the NOVA API did not answer" branch would swallow
      // the 403 that means "not on the investigator list", and those are
      // different sentences to different people.
      state: foReportsTile(foReports),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {tiles.map((tile) =>
        tile.state === "pending" ? (
          <Skeleton key={tile.key} height="7rem" />
        ) : (
          <DomainTile key={tile.key} spec={tile} content={tile.state} />
        ),
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ *
 * Cloud — three tiles off two feeds
 * ------------------------------------------------------------------------ */

/** The dark state the three Azure-backed tiles share. */
const AZURE_NOT_CONNECTED = { badge: "Azure not connected", icon: CloudCog } as const;

function alertsTile(data: CloudOverview): TileContent {
  if (!data.configured) {
    return darkTile(AZURE_NOT_CONNECTED, "Connect Azure on the Cloud board", "warning");
  }
  const firing = data.summary.firing_alert_count;
  if (firing == null) {
    // Unknown is not zero — the alerts snapshot has not landed yet.
    return darkTile(
      { badge: "Not collected yet", icon: CircleEllipsis },
      "The alert snapshot has not landed",
    );
  }
  return {
    value: formatCount(firing),
    tone: firing > 0 ? "danger" : "success",
    caption: firing > 0 ? "Azure Monitor alerts firing now" : "Azure Monitor · none firing",
  };
}

function subscriptionsTile(data: CloudOverview): TileContent {
  if (!data.configured) {
    return darkTile(AZURE_NOT_CONNECTED, "Connect Azure on the Cloud board", "warning");
  }
  const { subscriptions_expected: expected, subscriptions_reachable: reachable } = data.summary;
  if (expected === 0) {
    return darkTile(
      { badge: "None configured", icon: CloudCog },
      "No subscriptions in the registry yet",
    );
  }
  const missing = expected - reachable;
  return {
    value: `${reachable} of ${expected}`,
    tone: missing > 0 ? "danger" : "success",
    caption:
      missing > 0 ? `${missing} unreachable — partial estate view` : "All subscriptions answering",
  };
}

/** The month-to-date cost tile: value + day-over-day delta + inline trend. */
function costTile(data: CloudCost): TileContent {
  if (!data.configured) {
    return darkTile(AZURE_NOT_CONNECTED, "Connect Azure on the Cloud board", "warning");
  }
  if (data.month_to_date == null) {
    return darkTile(
      { badge: "Not collected yet", icon: CircleEllipsis },
      "No cost rows collected this month",
    );
  }

  // Day-over-day delta only when the two newest trend points share a month —
  // the series resets at the month boundary, so a cross-month diff is noise.
  const last = data.trend.at(-1);
  const previous = data.trend.at(-2);
  const sameMonth = last && previous && last.day.slice(0, 7) === previous.day.slice(0, 7);
  const diff = sameMonth ? last.actual_cost - previous.actual_cost : null;

  // Inline trend: only the collected points of the current month — days the
  // sweep skipped are simply absent, never interpolated to zero.
  const month = last?.day.slice(0, 7);
  const monthTrend = month
    ? data.trend.filter((point) => point.day.startsWith(month)).map((point) => point.actual_cost)
    : [];

  return {
    value: formatCurrency(data.month_to_date, data.currency),
    // Spend is not a status — the tile stays neutral (§3.2).
    tone: "neutral",
    caption: data.forecast
      ? `Forecast ${formatCurrencyCompact(data.forecast.month_total, data.forecast.currency)} this month`
      : "Month to date, actual",
    delta:
      diff == null
        ? undefined
        : `${diff >= 0 ? "+" : ""}${formatCurrencyCompact(diff, data.currency)} vs prior day`,
    trend:
      monthTrend.length >= 2 ? (
        <MiniSparkline
          data={monthTrend}
          tone="accent"
          width={120}
          height={20}
          endMarker="dot"
          aria-label="Month-to-date Azure cost, one point per collection day"
        />
      ) : undefined,
  };
}

/* ------------------------------------------------------------------------ *
 * Licences, websites, M365
 * ------------------------------------------------------------------------ */

function seatsTile(data: LicenseOverview): TileContent {
  if (!data.configured) {
    return darkTile(
      { badge: "Graph not connected", icon: KeyRound },
      "Connect Graph on the Licences board",
      "warning",
    );
  }
  const totals = data.totals;
  return {
    value: formatCount(totals.unused_seats),
    tone: totals.unused_seats > 0 ? "warning" : "success",
    caption:
      totals.unused_seats > 0
        ? totals.utilisation_pct != null
          ? `${formatPercent(totals.utilisation_pct)} of ${formatCount(totals.purchased_seats)} paid seats in use`
          : `${formatCount(totals.assigned_seats)} of ${formatCount(totals.purchased_seats)} paid seats assigned`
        : "Every paid seat assigned",
  };
}

/** The website poller is NOVA's own — it has no unconfigured state. */
function websitesTile(data: MonitoringOverview): TileContent {
  const summary = data.summary;
  if (summary.total === 0) {
    return darkTile(
      { badge: "No targets", icon: CircleEllipsis },
      "The website poller has no sites",
    );
  }
  if (summary.unknown === summary.total) {
    return darkTile(
      { badge: "Not checked yet", icon: CircleEllipsis },
      `${summary.total} sites, no checks`,
    );
  }
  const tone: StatisticTone =
    summary.down > 0 ? "danger" : summary.degraded > 0 ? "warning" : "success";
  return {
    value: `${summary.up} of ${summary.total}`,
    tone,
    caption:
      summary.down > 0 || summary.degraded > 0
        ? `${summary.down} down · ${summary.degraded} degraded`
        : summary.uptime_pct != null
          ? `${formatPercent(summary.uptime_pct)} uptime this window`
          : "All sites answering",
  };
}

function m365Tile(data: M365ServiceHealth): TileContent {
  if (!data.configured) {
    return darkTile(
      { badge: "Graph not connected", icon: KeyRound },
      "Connect Graph on the M365 board",
      "warning",
    );
  }
  if (data.status_reason !== "ok") {
    return darkTile(
      { badge: "Graph read failed", icon: ServerOff },
      "Service health could not be read",
      "warning",
    );
  }
  const summary = data.summary;
  if (summary.services_total === 0) {
    return darkTile(
      { badge: "No services", icon: CircleEllipsis },
      "Graph reported no services at all",
    );
  }
  const tone: StatisticTone =
    summary.services_interrupted > 0
      ? "danger"
      : summary.services_degraded > 0 || summary.active_incidents > 0
        ? "warning"
        : "success";
  return {
    value: `${summary.services_operational} of ${summary.services_total}`,
    tone,
    caption:
      summary.active_incidents > 0 || summary.active_advisories > 0
        ? `${summary.active_incidents} incidents · ${summary.active_advisories} advisories`
        : "All services operational",
  };
}

/* ------------------------------------------------------------------------ *
 * Network (Cato SASE)
 * ------------------------------------------------------------------------ */

/**
 * Cato needs two values and reports each separately, because they come from
 * different places: the API key from Key Vault, the numeric account ID from the
 * Cato Management Application URL. The badge names whichever is missing rather
 * than folding both into "not connected".
 */
function networkTile(data: NetworkOverview): TileContent {
  if (!data.configured) {
    // The final branch is unreachable while `configured` means "both halves
    // present", and is here so a change to that rule cannot make this tile claim
    // a specific missing value it has no evidence for.
    const missingKey = !data.api_key_present;
    const missingAccount = !data.account_id_present;
    const [badge, why] =
      missingKey && missingAccount
        ? (["Credential missing", "Cato API key and account ID both unset"] as const)
        : missingKey
          ? (["API key missing", "Cato API key is not injected into the backend"] as const)
          : missingAccount
            ? (["Account ID missing", "Cato account ID is not configured"] as const)
            : (["Cato not connected", data.status_detail] as const);
    return darkTile({ badge, icon: KeyRound }, why, "warning");
  }

  const counts = stateCounts(data.summary.counts);
  const total = data.summary.counts.total ?? 0;
  if (total === 0) {
    return darkTile(
      { badge: "No sites returned", icon: CircleEllipsis },
      "Cato answered with no sites",
    );
  }

  // A disabled site is down on purpose, so it never drives the tone — that
  // precedence is the Network board's own (`STATE_TONE` in its status module).
  const tone =
    counts.disconnected > 0
      ? STATE_TONE.disconnected
      : counts.degraded > 0
        ? STATE_TONE.degraded
        : STATE_TONE.connected;

  return {
    value: `${counts.connected} of ${total}`,
    tone,
    caption:
      counts.disconnected > 0 || counts.degraded > 0
        ? `${counts.disconnected} disconnected · ${counts.degraded} degraded`
        : counts.disabled > 0
          ? `${counts.disabled} disabled (not a fault)`
          : "Every site reporting to Cato",
  };
}

/* ------------------------------------------------------------------------ *
 * Email security — two boards, one credential
 * ------------------------------------------------------------------------ */

/**
 * Both TAP boards ship dark for the same reason, so both say so in the same
 * words — and the second one says it is the same credential, rather than reading
 * as a second, separate fault. Same reasoning as the `repeated` mode of
 * `features/dynamics/access-panel.tsx`.
 *
 * Awaiting a rotation is an action item, not an outage, so it is `warning` and
 * never `danger`.
 */
const TAP_AWAITING_CREDENTIAL = { badge: "Awaiting credential", icon: KeyRound } as const;

function securityTile(data: SecurityOverview): TileContent {
  if (!data.configured) {
    return darkTile(
      TAP_AWAITING_CREDENTIAL,
      "TAP credential is tagged for rotation, not wired in",
      "warning",
    );
  }
  // A stalled poller, an open circuit or an unreadable store all mean the counts
  // below are a frozen snapshot rather than a measurement of now. The Security
  // board owns that judgement; the tile borrows it instead of guessing.
  if (!figuresAreCurrent(data)) {
    return darkTile(
      { badge: "Figures not current", icon: ServerOff },
      "The TAP poller has not completed recently",
      "warning",
    );
  }

  // Documented on the payload as "set when the store could not be read, so zeros
  // are not read as calm" — so it must not render as a calm zero here either.
  if (data.health.store_detail) {
    return darkTile(
      { badge: "Store unreadable", icon: ServerOff },
      "Stored daily counts could not be read",
      "warning",
    );
  }

  // One tile, because there is now one board. The overview used to carry a
  // Security tile (delivered share) beside a Proofpoint tile (reached a user)
  // — two doors onto the same TAP data, which is the duplication the
  // 2026-08-20 merge removed. "Reached a user" is the figure that survives:
  // it is an absolute count in the deck's own language rather than a ratio,
  // and a ratio hides the thing leadership asks about first, which is how
  // many dangerous messages actually landed.
  const summary = data.summary;
  return {
    value: formatCount(summary.reached_user),
    tone: reachedUserTone(summary.reached_user),
    caption:
      summary.reached_user === 0
        ? `Nothing got through in ${summary.window_days} days`
        : `${formatCount(summary.delivered)} delivered · ${formatCount(summary.clicks_permitted)} clicks permitted`,
  };
}

/* ------------------------------------------------------------------------ *
 * Dynamics 365 F&O — two workspaces, two independent access states
 * ------------------------------------------------------------------------ */

/**
 * Turn an `access_state` into a dark tile using the Dynamics board's own copy.
 *
 * This is the reason the two F&O tiles exist separately. The telemetry workspace
 * and the Service Bus workspace fail differently and need different people: a
 * 403 is a `Log Analytics Reader` assignment an Owner grants, a 404 is a resource
 * ID in configuration that is wrong. `ACCESS_COPY` already distinguishes all six
 * states with a tone, an icon, a short badge and a title, so the tile takes all
 * four from there — the overview cannot drift from the board it links to, and
 * neither state can be flattened into "unavailable".
 */
function foAccessTile(state: FoOverview["access_state"]): TileContent {
  const copy = ACCESS_COPY[state];
  return darkTile({ badge: copy.badge, icon: copy.icon }, copy.title, copy.tone);
}

/** The worst verdict present in a set of F&O health states, or `null` if empty. */
function worstHealth(states: readonly string[]) {
  return HEALTH_ORDER.find((candidate) => states.includes(candidate)) ?? null;
}

function foTelemetryTile(data: FoOverview): TileContent {
  if (data.access_state !== "ok") {
    return foAccessTile(data.access_state);
  }

  const environments = data.environments;
  if (environments.length === 0) {
    return darkTile(
      { badge: "No environments", icon: CircleEllipsis },
      "Telemetry read, but no environment reported",
    );
  }

  const worst = worstHealth(environments.map((environment) => environment.state));
  const healthy = environments.filter((environment) => environment.state === "healthy").length;
  // A partial snapshot makes the count a floor, not a census (§8).
  const partial = data.errors.length > 0 ? " · snapshot incomplete" : "";

  return {
    value: `${healthy} of ${environments.length}`,
    tone: worst == null ? "neutral" : HEALTH_TONE[worst],
    caption:
      // "answering", not "healthy". This tile is built from the synthetic probe
      // and a throttling count — a URL ping that follows redirects and never
      // signs in — and it links straight at the section now titled Control,
      // which exists to say that a green probe rules out the network path and
      // the front door and NOTHING about the application tier. This is the
      // highest-visibility surface in the product, read by the people least
      // placed to discount it, so it was the worst place left for the claim.
      worst === "healthy"
        ? `Every environment answered the probe${partial}`
        : `Worst: ${HEALTH_LABEL[worst ?? "unknown"]}${partial}`,
  };
}

function foIntegrationTile(data: FoIntegration): TileContent {
  if (data.access_state !== "ok") {
    return foAccessTile(data.access_state);
  }

  const queues = data.queues;
  if (queues.length === 0) {
    return darkTile(
      { badge: "No queues", icon: CircleEllipsis },
      `${data.namespace} produced no queue metrics`,
    );
  }

  const worst = worstHealth(queues.map((queue) => queue.state));
  const healthy = queues.filter((queue) => queue.state === "healthy").length;
  const partial = data.errors.length > 0 ? " · snapshot incomplete" : "";

  return {
    value: `${healthy} of ${queues.length}`,
    tone: worst == null ? "neutral" : HEALTH_TONE[worst],
    caption:
      worst === "healthy"
        ? `Every Service Bus queue healthy${partial}`
        : `Worst: ${HEALTH_LABEL[worst ?? "unknown"]}${partial}`,
  };
}

/* ------------------------------------------------------------------------ *
 * F&O observability — the console, the browser fleet, the report queue
 * ------------------------------------------------------------------------ */

/**
 * The one map from a named dark icon to a glyph.
 *
 * `fo-tile-format.ts` names the *kind* of problem and never touches an icon
 * value, which is what keeps it importable by `node --test`. This is where the
 * name becomes a component, and the four kinds line up with the tone scale in
 * `./tile-state.ts`: pending is nobody's fault, credential and forbidden are a
 * human action somewhere, unreadable is a fault that will not fix itself.
 */
const DARK_ICON: Record<DarkIcon, IconComponent> = {
  credential: KeyRound,
  pending: CircleEllipsis,
  unreadable: ServerOff,
  forbidden: Lock,
};

/** Lift the pure module's facts into the tile contract the grid renders. */
function fromFacts(facts: FoTileFacts): TileContent {
  return {
    value: facts.value,
    tone: facts.tone,
    caption: facts.caption,
    dark: facts.dark ? { badge: facts.dark.badge, icon: DARK_ICON[facts.dark.icon] } : undefined,
  };
}

function foSlowTile(data: FoConsoleSlowSummary): TileContent {
  const facts = slowInteractionsTile(data, { coverageText: formatCoverage(data.coverage) });
  // The board's own access copy — badge, tone, icon and sentence — rather than a
  // second opinion, so the overview and the board it links to cannot disagree
  // about why the console is dark. Same device as the two workspace tiles above.
  return facts === ACCESS_STATE_TILE ? foAccessTile(data.access_state) : fromFacts(facts);
}

function foBrowserCoverageTile(data: FoConsoleRumStatus): TileContent {
  return fromFacts(browserCoverageTile(data, rumRolloutState(data)));
}

/**
 * The report queue's tile, resolved without `resolveTile`.
 *
 * A thrown 403 is not a broken feed — it is the answer "the queue exists and you
 * may not read it", which needs its own words and its own badge. `resolveTile`
 * would render both that and a genuine outage as "Feed unreachable", sending
 * half the readers to the wrong conclusion.
 */
function foReportsTile(
  feed: FeedState<FoConsoleReportList> & { error?: unknown },
): TileContent | "pending" {
  const status = apiErrorStatus(feed.error);
  if (feed.data === undefined && !feed.isError) {
    return "pending";
  }
  return fromFacts(
    openReportsTile(feed.data, status, reportLookbackOption(DEFAULT_REPORT_LOOKBACK).caption),
  );
}

/* ------------------------------------------------------------------------ *
 * The tile itself
 * ------------------------------------------------------------------------ */

/**
 * `IconTone` has no `neutral` member, so a neutral tile's glyph inherits the
 * card's own text colour rather than reaching for a token that does not exist.
 */
function iconTone(tone: StatisticTone): IconTone | undefined {
  return tone === "neutral" ? undefined : tone;
}

function DomainTile({ spec, content }: { spec: TileSpec; content: TileContent }) {
  const dark = content.dark;

  return (
    <Link to={spec.to} aria-label={spec.ariaLabel} className="block h-full">
      <Card
        // The recessed fill is what says "nothing was measured here" — the tone
        // beside it says how bad that is. See `./tile-state.ts`.
        variant={dark ? "subtle" : "outline"}
        interactive
        accent="left"
        accentTone={content.tone}
        className="h-full"
      >
        <Card.Body>
          <div className="flex flex-col gap-2">
            {dark && (
              <div className="flex items-center gap-1.5">
                <Icon icon={dark.icon} size="sm" tone={iconTone(content.tone)} />
                <Badge tone={content.tone} variant="outline" size="xs">
                  {dark.badge}
                </Badge>
              </div>
            )}
            <Statistic
              label={spec.label}
              value={content.value}
              tone={content.tone}
              size="md"
              caption={content.caption}
              delta={content.delta}
            />
            {content.trend}
          </div>
        </Card.Body>
      </Card>
    </Link>
  );
}
